/**
 * Tariffs Redux Slice
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
export interface Tariff {
  id: number;
  contract_line_id: number;
  line_number: string | null;
  service_type_id: number | null;
  origin_port_id: number | null;
  destination_port_id: number | null;
  incoterm_id: number | null;
  service_terminal_id: number | null;
  free_time: number | null;
  transit_time: number | null;
  frequency: string | null;
  operation_type: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
// Tipos de respuesta
interface TariffsResponse {
  data: Tariff[];
  totalCount: number;
}
interface TariffsState extends FilteredState<Tariff> {
  selectedTariff: Tariff | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
}
const initialState: TariffsState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'line_number',
  sortDirection: 'desc',
  searchTerm: null,
  selectedTariff: null,
  creating: false,
  updating: false,
  deleting: false,
};
export const fetchTariffs = createAsyncThunk<TariffsResponse, any>('tariffs/fetchTariffs', async (params) => {
  throw new Error('Not implemented yet');
});
export const fetchTariffById = createAsyncThunk<Tariff, number>('tariffs/fetchTariffById', async (id) => {
  throw new Error('Not implemented yet');
});
export const createTariff = createAsyncThunk<Tariff, Partial<Tariff>>('tariffs/createTariff', async (tariff) => {
  throw new Error('Not implemented yet');
});
export const updateTariff = createAsyncThunk<{ id: number; data: Tariff }, { id: number; data: Partial<Tariff> }>('tariffs/updateTariff', async ({ id, data }) => {
  throw new Error('Not implemented yet');
});
export const deleteTariff = createAsyncThunk<number, number>('tariffs/deleteTariff', async (id) => {
  throw new Error('Not implemented yet');
});
const tariffsSlice = createSlice({
  name: 'tariffs',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectTariff: (state, action: PayloadAction<Tariff | null>) => {
      state.selectedTariff = action.payload;
    },
    clearSelection: (state) => {
      state.selectedTariff = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTariffs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTariffs.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchTariffs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar tarifas';
      });
    // Similar para otros casos...
  },
});
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectTariff, clearSelection } = tariffsSlice.actions;
export default tariffsSlice.reducer;
